<template>
  <div class="flex flex-col sm:space-y-5 space-y-3.5">
    <div class="grid md:grid-cols-2 grid-cols-1 md:gap-5 gap-3.5">
      <BaseSearchSelect
        :title="$t('new.select_country')"
        :label="$t('new.country_label')"
        :loading="isLoadingRegions"
        :empty-list-title="$t('new.no_country')"
        :items="filteredCountries"
        :height="500"
        :placeholder="$t('new.search_country')"
        @select="selectCountry"
        v-model:search="countrySearch"
      >
        <template #select-title>
          <div class="flex items-center">
            <div class="flex items-center space-x-2">
              <BaseImage
                @contextmenu.prevent
                @dragstart.prevent
                img-class="h-5 w-8 rounded-xs object-cover border border-gray-100"
                :uri="getImageUrl(getSelectedCountry.code)"
                :alt="getSelectedCountry.code"
              />
              <span :title="getSelectedCountry.native">{{
                cutName(
                  getSelectedCountry.label[locale as Locale] ||
                    getSelectedCountry.label.hy ||
                    getSelectedCountry.label.en ||
                    getSelectedCountry.label.ru ||
                    "",
                  15,
                )
              }}</span>
            </div>
          </div>
        </template>
        <template #item="{ item }">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <BaseImage
                @contextmenu.prevent
                @dragstart.prevent
                img-class="h-5 w-8 rounded-xs object-cover border border-gray-100"
                :uri="getImageUrl(item.code)"
                :alt="item.code"
              />
              <span :title="item.native">{{
                cutName(
                  item.label[locale] ||
                    item.label.hy ||
                    item.label.en ||
                    item.label.ru ||
                    "",
                  15,
                )
              }}</span>
            </div>
            <span :title="item.native" class="text-sm text-gray-500"
              >({{ cutName(item.native, 12) }})</span
            >
          </div>
        </template>
      </BaseSearchSelect>

      <BaseSearchSelect
        :title="$t('new.select_city')"
        :label="$t('new.city_label')"
        :loading="isLoadingRegions"
        :items="filteredRegions"
        :placeholder="$t('new.search_city')"
        :empty-list-title="$t('new.no_city')"
        :height="500"
        @select="selectRegion"
        v-model:search="regionSearch"
      >
        <template #select-title>
          <div class="">
            {{ cutName(getSelectedCity, 25) }}
          </div>
        </template>
        <template #item="{ item }">
          <div class="flex items-center justify-between">
            {{ getRegionLabel(item) || item?.en || $t("new.select_city") }}
          </div>
        </template>
      </BaseSearchSelect>
    </div>

    <BaseSearchSelect
      :title="$t('new.select_venue')"
      :label="$t('new.venue_label')"
      :items="filteredVenues"
      :custom-enambled="true"
      :empty-list-title="$t('new.no_venue')"
      :height="500"
      :placeholder="$t('new.search_venue')"
      :custom-placeholder="$t('new.custom_venue_placeholder')"
      :custom-label="$t('new.custom_input')"
      @select="selectVenue"
      @custom-mode="onVenueCustomMode"
      v-model:search="venueSearch"
      v-model:custom-value="venueCustomValue"
    >
      <template #select-title>
        <div class="">
          {{ cutName(getSelectedArtVenue, 25) }}
        </div>
      </template>
      <template #item="{ item }">
        <div class="flex items-center justify-between">
          {{ cutName(item.title, 30) }}
        </div>
      </template>
    </BaseSearchSelect>
  </div>
</template>

<script setup lang="ts">
import { COUNTRIES, type ICountry } from "@/data/countries";
import { ARMENIA_REGIONS } from "@/data/regions";
import { GALLERIES, type GalleryItem } from "@/data/gallery";
import { cutName } from "../../utils/helpers";
import type { Locale } from "~~/types/global";
import { onMounted, ref } from "vue";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { LocalizedField } from "~~/types/metadata";
import { useMessage } from "~/stores/app-toast";

const { locale, t } = useI18n();
const store = useNewArtworkStore();
const countrySearch = ref<string>("");
const regionSearch = ref<string>("");
const venueSearch = ref<string>("");
const apiRegions = ref<LocalizedField[]>([]);
const isLoadingRegions = ref(false);
const isVenueCustom = ref(false);
const venueCustomValue = ref<string>();

const toast = useMessage();

const filteredCountries = computed<ICountry[]>(() => {
  const search = countrySearch.value.trim().toLowerCase();

  // If no search, return the whole list (or a slice for performance)
  if (!search) return COUNTRIES;

  return COUNTRIES.filter((country) => {
    // Check all three languages
    return (
      country.label.en.toLowerCase().includes(search) ||
      country.label.hy.toLowerCase().includes(search) ||
      country.label.ru.toLowerCase().includes(search) ||
      country.code.toLowerCase().includes(search) // Also allow searching by "am" or "ru"
    );
  });
});

const filteredRegions = computed<LocalizedField[]>(() => {
  const search = regionSearch.value.trim().toLowerCase();
  const selectedCountryCode = getSelectedCountry.value.code.toLowerCase();

  const sourceList =
    selectedCountryCode === "am" ? ARMENIA_REGIONS : apiRegions.value;

  if (!sourceList?.length) return [];
  if (!search) return sourceList;

  return sourceList.filter((region) => {
    return (
      region.en?.toLowerCase().includes(search) ||
      region.hy?.toLowerCase().includes(search) ||
      region.ru?.toLowerCase().includes(search)
    );
  });
});

const filteredVenues = computed<GalleryItem[]>(() => {
  if (!venueSearch.value) return GALLERIES;
  return GALLERIES.filter((item) => {
    return item.title
      .toLocaleLowerCase()
      .includes(venueSearch.value.toLocaleLowerCase());
  });
});

const getSelectedCountry = computed<ICountry>(() => {
  const targetCode = store.newArtwork?.location?.countryCode;

  // 1. Try to find the user's selected country
  const found = COUNTRIES.find((c: ICountry) => c.code === targetCode);
  if (found) return found;

  // 2. Try to find Armenia as a default
  const armenia = COUNTRIES.find((c: ICountry) => c.code === "am");
  if (armenia) return armenia;

  // 3. IMPORTANT: If the array is still empty (loading), return a dummy object
  // This prevents .code from being undefined
  return {
    code: "am",
    label: { en: "Armenia", hy: "Հայաստան", ru: "Армения" },
  } as ICountry;
});

const getSelectedCity = computed<string>(
  () =>
    store?.newArtwork?.location?.city?.[locale.value as Locale] ||
    store?.newArtwork?.location?.city?.en ||
    t("new.select_city"),
);

const getSelectedArtVenue = computed<string>(
  () => store?.newArtwork?.location?.venue?.title ?? t("new.select_venue"),
);

const selectCountry = async (c: ICountry) => {
  const code = c.code;
  store.setCountry(code);
  await fetchRegionsByCountry(code);
  store.setRegion({ en: "", hy: "", ru: "" });
};

const selectRegion = (r: LocalizedField) => {
  store.setRegion(r);
};
const getRegionLabel = (item: LocalizedField) => {
  return item[locale.value as Locale] || item.en;
};
const fetchRegionsByCountry = async (code: string) => {
  const countryCode = code.toUpperCase();
  const { $api } = useNuxtApp();
  // 1. Handle Armenia locally (Zero API cost/latency)
  if (countryCode === "AM") {
    apiRegions.value = ARMENIA_REGIONS;
    return;
  }

  try {
    // 2. Fetch from the API for other countries
    const response = await $api<any>(`/cities/${countryCode}`);

    if (!response.data) return;

    const cities = response.data;

    if (!cities || cities.length === 0) {
      const noCityCountry = {
        en: "Country does not have city",
        hy: "Երկիրը քաղաքներ չունի",
        ru: "В стране нет городов",
      };
      apiRegions.value = [noCityCountry];
      store.setRegion(noCityCountry);
      return;
    }

    // 3. Map the data to your format
    apiRegions.value = cities;
  } catch (error) {
    toast.add(
      "Սխալ առաջացավ խնդրում ենք փորձել նորից կամ թարմացնել էջը",
      "error",
    );
    apiRegions.value = [];
  }
};

const getImageUrl = (code: string) => {
  return `https://raw.githubusercontent.com/hampusborgos/country-flags/main/svg/${code.toLowerCase()}.svg`;
};

const onVenueCustomMode = (custom: boolean) => {
  isVenueCustom.value = custom;
  store.setVenue({ title: "", type: "other" });
};

const selectVenue = (v: GalleryItem) => {
  store.setVenue(v);
};

watch([isVenueCustom, venueCustomValue], ([isCustom, customVal]) => {
  if (isCustom) {
    store.setVenue({ title: customVal || "", type: "other" });
  } else {
    store.setVenue({
      title: "Անձնական հավաքածու",
      type: "private",
    });
    venueCustomValue.value = "";
  }
});

onMounted(async () => {
  const countryCode = store?.newArtwork?.location?.countryCode || "am";
  await fetchRegionsByCountry(countryCode);
});
</script>
